/**
 * Esc STOPS THE CHAT'S TURN, as the Stop button does (Tom, 2026-10-20: "add
 * the Esc key as a way to stop the chat for power user familiarity"). The
 * chat view calls this from its own key handler, so only a key pressed with
 * focus inside that chat reaches it, and it calls the Stop button's own
 * handler when this answers true.
 *
 * Never when something else takes the Esc: an open autocomplete, dialog or
 * menu closes first (they mark the key handled, and a key pressed inside one
 * is left to it); never from a form control other than the composer (the
 * model select, an in-place message edit: an Esc there means "leave this",
 * 27 Oct a turn was stopped that way); never while an input method is composing text; never
 * when no turn is running or waiting (`active`: when Stop is offered); and
 * a held key does not repeat it.
 */
export type EscKey = {
  key: string
  defaultPrevented: boolean
  repeat?: boolean
  target: EventTarget | null
  nativeEvent?: { isComposing?: boolean; keyCode?: number }
}

const CONSUMERS = '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"],[role="combobox"][aria-expanded="true"]'
/** form controls keep their Esc; the composer opts in (`data-esc-stops-turn`) */
const CONTROLS = 'select,input,textarea,[contenteditable]:not([contenteditable="false"])'
const COMPOSER = '[data-esc-stops-turn]'

export function escStopsTheTurn(e: EscKey, active: boolean): boolean {
  if (e.key !== 'Escape' || !active) return false
  if (e.defaultPrevented || e.repeat) return false
  // an input method composing text uses Esc to cancel the composition
  if (e.nativeEvent?.isComposing || e.nativeEvent?.keyCode === 229) return false
  const t = e.target as Element | null
  if (t && typeof t.closest === 'function') {
    if (t.closest(CONSUMERS)) return false
    if (t.closest(CONTROLS) && !t.closest(COMPOSER)) return false
  }
  return true
}
